import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { 
  TrendingUp, TrendingDown, DollarSign, AlertTriangle, 
  Users, Calendar, Target, RefreshCw, X 
} from 'lucide-react';
import { formatCurrency } from '@/lib/utils';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';
import GeoHotspotsWidget from '@/components/GeoHotspotsWidget';
import RenewalMap from '@/components/RenewalMap';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const COLORS = ['#09409C', '#146CBB', '#FF521C', '#229644', '#FFA500'];

export default function ExecutiveDashboard() {
  const navigate = useNavigate();
  const [showFullMap, setShowFullMap] = useState(false);
  
  const { data: summaryData, isLoading: summaryLoading } = useQuery({
    queryKey: ['dashboard-summary'],
    queryFn: async () => {
      const token = localStorage.getItem('ccip_token');
      const response = await axios.get(`${BACKEND_URL}/api/dashboard/summary`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data.data;
    },
    refetchInterval: 60000, // Refresh every minute
  });

  const { data: forecastData } = useQuery({
    queryKey: ['dashboard-forecast'],
    queryFn: async () => {
      const token = localStorage.getItem('ccip_token');
      const response = await axios.get(`${BACKEND_URL}/api/dashboard/forecast?months=6`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data.data;
    },
  });

  const { data: pipelineData } = useQuery({
    queryKey: ['dashboard-pipeline'],
    queryFn: async () => {
      const token = localStorage.getItem('ccip_token');
      const response = await axios.get(`${BACKEND_URL}/api/dashboard/pipeline`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    },
  });

  const { data: teamData } = useQuery({
    queryKey: ['dashboard-team'],
    queryFn: async () => {
      const token = localStorage.getItem('ccip_token');
      const response = await axios.get(`${BACKEND_URL}/api/dashboard/team-performance`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data.data;
    },
  });

  if (summaryLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <RefreshCw className="w-8 h-8 animate-spin text-[#09409C]" />
      </div>
    );
  }

  const portfolioHealth = summaryData?.portfolioHealth || {};
  const valueAtRisk = summaryData?.valueAtRisk || {};
  const vipWatchList = summaryData?.vipWatchList || [];

  return (
    <div className="p-8 space-y-6" data-testid="executive-dashboard">
      {/* Header */}
      <div>
        <h1 className="text-4xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
          <span className="text-gradient-primary">Executive Dashboard</span>
        </h1>
        <p className="text-muted-foreground mt-2 text-lg">
          Real-time portfolio insights and performance metrics
        </p>
      </div>

      {/* Top KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {/* Portfolio Health */}
        <Card className="border-2 border-blue-200 hover:shadow-lg transition-shadow">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-medium text-muted-foreground">Portfolio Health</p>
              <Target className="w-5 h-5 text-[#09409C]" />
            </div>
            <div className="text-4xl font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {portfolioHealth.score || 0}
            </div>
            <div className="flex items-center text-sm text-green-600">
              <TrendingUp className="w-4 h-4 mr-1" />
              {portfolioHealth.trend || '+0%'}
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              {portfolioHealth.totalContracts || 0} active contracts
            </p>
          </CardContent>
        </Card>

        {/* Value at Risk */}
        <Card className="border-2 border-red-200 hover:shadow-lg transition-shadow">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-medium text-muted-foreground">Value at Risk</p>
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>
            <div className="text-3xl font-bold mb-2 text-red-700" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {formatCurrency(valueAtRisk.amount || 0)}
            </div>
            <p className="text-sm text-muted-foreground">
              {valueAtRisk.contractCount || 0} contracts at risk
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {valueAtRisk.percentage || 0}% of portfolio
            </p>
          </CardContent>
        </Card>

        {/* Total Portfolio Value */}
        <Card className="border-2 border-green-200 hover:shadow-lg transition-shadow">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-medium text-muted-foreground">Portfolio Value</p>
              <DollarSign className="w-5 h-5 text-[#229644]" />
            </div>
            <div className="text-3xl font-bold mb-2 text-[#229644]" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {formatCurrency(portfolioHealth.totalValue || 0)}
            </div>
            <p className="text-sm text-muted-foreground">
              {portfolioHealth.totalContracts || 0} contracts
            </p>
          </CardContent>
        </Card>

        {/* VIP Contracts */}
        <Card className="border-2 border-purple-200 hover:shadow-lg transition-shadow">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-medium text-muted-foreground">VIP Expiring Soon</p>
              <Users className="w-5 h-5 text-purple-600" />
            </div>
            <div className="text-4xl font-bold mb-2 text-purple-700" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {vipWatchList.length}
            </div>
            <p className="text-sm text-muted-foreground">
              Require immediate attention
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Renewal Pipeline */}
        {pipelineData?.pipeline && (
          <Card className="col-span-2">
            <CardHeader>
              <CardTitle className="text-xl" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Renewal Pipeline
              </CardTitle>
              {pipelineData.summary && (
                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                  <span>Total in Pipeline: <strong>{pipelineData.summary.totalInPipeline}</strong></span>
                  <span>Pipeline Value: <strong>{formatCurrency(pipelineData.summary.totalPipelineValue)}</strong></span>
                  <span>Renewal Rate: <strong className="text-green-600">{pipelineData.summary.renewalRate}%</strong></span>
                </div>
              )}
            </CardHeader>
            <CardContent>
              {/* Pipeline Stages */}
              <div className="grid grid-cols-5 gap-3 mb-6">
                {/* Pending */}
                <div className="p-3 rounded-lg border-2 border-gray-200 bg-gray-50">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-gray-600">Pending</span>
                    <Badge variant="outline">{pipelineData.pipeline.pending?.count || 0}</Badge>
                  </div>
                  <div className="text-lg font-bold text-gray-800">
                    {formatCurrency(pipelineData.pipeline.pending?.totalValue || 0)}
                  </div>
                </div>
                
                {/* In Progress */}
                <div className="p-3 rounded-lg border-2 border-blue-200 bg-blue-50">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-blue-600">In Progress</span>
                    <Badge className="bg-blue-100 text-blue-800">{pipelineData.pipeline.inProgress?.count || 0}</Badge>
                  </div>
                  <div className="text-lg font-bold text-blue-800">
                    {formatCurrency(pipelineData.pipeline.inProgress?.totalValue || 0)}
                  </div>
                </div>
                
                {/* Snoozed */}
                <div className="p-3 rounded-lg border-2 border-orange-200 bg-orange-50">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-orange-600">Snoozed</span>
                    <Badge className="bg-orange-100 text-orange-800">{pipelineData.pipeline.snoozed?.count || 0}</Badge>
                  </div>
                  <div className="text-lg font-bold text-orange-800">
                    {formatCurrency(pipelineData.pipeline.snoozed?.totalValue || 0)}
                  </div>
                </div>
                
                {/* Renewed */}
                <div className="p-3 rounded-lg border-2 border-green-200 bg-green-50">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-green-600">Renewed</span>
                    <Badge className="bg-green-100 text-green-800">{pipelineData.pipeline.completed?.renewed?.count || 0}</Badge>
                  </div>
                  <div className="text-lg font-bold text-green-800">
                    {formatCurrency(pipelineData.pipeline.completed?.renewed?.totalValue || 0)}
                  </div>
                </div>
                
                {/* Lost */}
                <div className="p-3 rounded-lg border-2 border-red-200 bg-red-50">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-red-600">Lost</span>
                    <Badge className="bg-red-100 text-red-800">{pipelineData.pipeline.completed?.lost?.count || 0}</Badge>
                  </div>
                  <div className="text-lg font-bold text-red-800">
                    {formatCurrency(pipelineData.pipeline.completed?.lost?.totalValue || 0)}
                  </div>
                </div>
              </div>
              
              {/* Pipeline Funnel Visualization */}
              <ResponsiveContainer width="100%" height={200}>
                <BarChart 
                  data={[
                    { name: 'Pending', value: pipelineData.pipeline.pending?.totalValue || 0, count: pipelineData.pipeline.pending?.count || 0 },
                    { name: 'In Progress', value: pipelineData.pipeline.inProgress?.totalValue || 0, count: pipelineData.pipeline.inProgress?.count || 0 },
                    { name: 'Snoozed', value: pipelineData.pipeline.snoozed?.totalValue || 0, count: pipelineData.pipeline.snoozed?.count || 0 },
                    { name: 'Renewed', value: pipelineData.pipeline.completed?.renewed?.totalValue || 0, count: pipelineData.pipeline.completed?.renewed?.count || 0 },
                    { name: 'Lost', value: pipelineData.pipeline.completed?.lost?.totalValue || 0, count: pipelineData.pipeline.completed?.lost?.count || 0 },
                  ]}
                  layout="vertical"
                >
                  <XAxis type="number" tickFormatter={(value) => `$${(value / 1000000).toFixed(1)}M`} />
                  <YAxis type="category" dataKey="name" width={80} />
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                  <Bar dataKey="value" fill="#09409C" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {/* Renewal Forecast */}
        {forecastData && (
          <Card>
            <CardHeader>
              <CardTitle className="text-xl" style={{ fontFamily: 'Manrope, sans-serif' }}>
                6-Month Renewal Forecast
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={forecastData}>
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                  <Bar dataKey="expected.value" stackId="a" fill="#229644" name="Expected" />
                  <Bar dataKey="atRisk.value" stackId="a" fill="#FFA500" name="At Risk" />
                  <Bar dataKey="likelyLost.value" stackId="a" fill="#FF521C" name="Likely Lost" />
                </BarChart>
              </ResponsiveContainer>
              <div className="flex items-center justify-center gap-4 mt-4">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-[#229644] rounded" />
                  <span className="text-xs">Expected</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-[#FFA500] rounded" />
                  <span className="text-xs">At Risk</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-[#FF521C] rounded" />
                  <span className="text-xs">Likely Lost</span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Team Performance */}
        {teamData && teamData.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-xl" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Team Performance
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={teamData.slice(0, 5)} layout="vertical">
                  <XAxis type="number" />
                  <YAxis dataKey="name" type="category" width={120} />
                  <Tooltip />
                  <Bar dataKey="renewalRate" fill="#146CBB" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {/* VIP Watch List */}
        <Card>
          <CardHeader>
            <CardTitle className="text-xl flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <Users className="w-5 h-5 text-purple-600" />
              VIP Watch List
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {vipWatchList.length > 0 ? (
                vipWatchList.map((item) => (
                  <div
                    key={item.contract_id}
                    className="flex items-center justify-between p-3 rounded-lg border hover:border-[#09409C] transition-colors cursor-pointer"
                    onClick={() => navigate(`/contracts/${item.contract_id}`)}
                  >
                    <div>
                      <p className="font-semibold">{item.account_name}</p>
                      <p className="text-sm text-muted-foreground">{item.contract_number}</p>
                    </div>
                    <div className="text-right">
                      <Badge className={item.days_left <= 30 ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'}>
                        {item.days_left} days
                      </Badge>
                      <p className="text-sm text-muted-foreground mt-1">
                        {formatCurrency(item.annual_value)}
                      </p>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-center text-muted-foreground py-8">No VIP contracts expiring soon</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Geographic Hotspots Widget */}
        <GeoHotspotsWidget onExpandMap={() => setShowFullMap(true)} />
      </div>

      {/* Full Map Dialog */}
      <Dialog open={showFullMap} onOpenChange={setShowFullMap}>
        <DialogContent className="max-w-6xl h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Contract Geographic Distribution
            </DialogTitle>
            <DialogDescription>
              Interactive map showing contract locations across the United States with health status indicators
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 h-full min-h-[500px]">
            <RenewalMap 
              height="100%"
              showLegend={true}
              showStats={true}
              onViewDetails={(contractId) => {
                setShowFullMap(false);
                navigate(`/contracts/${contractId}`);
              }}
              interactive={true}
              className="h-full"
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
